import { useState, useEffect } from 'react';

interface ImageViewerProps {
  images: string[];
  thumbnails: string[];
}

export default function ImageViewer({ images, thumbnails }: ImageViewerProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const [imgError, setImgError] = useState<Record<number, boolean>>({});
  const visibleCount = 6;

  useEffect(() => {
    setActiveIndex(0);
    setThumbStart(0);
    setImgError({});
  }, [images]);

  const handlePrev = () => {
    if (thumbStart > 0) setThumbStart(thumbStart - 1);
  };

  const handleNext = () => {
    if (thumbStart + visibleCount < thumbnails.length) setThumbStart(thumbStart + 1);
  };

  const handleMainPrev = () => {
    const newIdx = (activeIndex - 1 + images.length) % images.length;
    setActiveIndex(newIdx);
    if (newIdx < thumbStart) setThumbStart(newIdx);
  };

  const handleMainNext = () => {
    const newIdx = (activeIndex + 1) % images.length;
    setActiveIndex(newIdx);
    if (newIdx >= thumbStart + visibleCount) setThumbStart(newIdx - visibleCount + 1);
  };

  return (
    <div className="w-full">
      {/* Main Image */}
      <div className="relative flex justify-center mb-4">
        <div className="w-full max-w-3xl aspect-[3/2] overflow-hidden rounded-xl bg-gray-100 relative">
          {!imgError[activeIndex] ? (
            <img
              src={images[activeIndex]}
              alt={`시공사례 이미지 ${activeIndex + 1}`}
              className="w-full h-full object-contain object-center transition-opacity duration-300"
              onError={() => setImgError((prev) => ({ ...prev, [activeIndex]: true }))}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
              <i className="ri-image-line text-4xl" />
              <span className="text-xs">이미지를 불러올 수 없습니다</span>
            </div>
          )}

          {/* Prev / Next overlay buttons */}
          <button
            onClick={handleMainPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-700 transition-colors cursor-pointer"
            aria-label="이전 이미지"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            onClick={handleMainNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-700 transition-colors cursor-pointer"
            aria-label="다음 이미지"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>

          {/* Counter badge */}
          <span className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {activeIndex + 1} / {images.length}
          </span>
        </div>
      </div>

      {/* Thumbnail Strip */}
      <div className="bg-gray-50 rounded-xl p-3">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={thumbStart === 0}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
            aria-label="이전"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>

          <div className="flex gap-2 flex-1 overflow-hidden">
            {thumbnails.slice(thumbStart, thumbStart + visibleCount).map((thumb, idx) => {
              const realIdx = thumbStart + idx;
              return (
                <button
                  key={realIdx}
                  onClick={() => setActiveIndex(realIdx)}
                  className={`flex-1 aspect-square overflow-hidden rounded-lg border-2 transition-all cursor-pointer ${
                    activeIndex === realIdx
                      ? 'border-yellow-500 opacity-100'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  {!imgError[realIdx] ? (
                    <img
                      src={thumb}
                      alt={`썸네일 ${realIdx + 1}`}
                      className="w-full h-full object-cover object-center"
                      onError={() => setImgError((prev) => ({ ...prev, [realIdx]: true }))}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-300">
                      <i className="ri-image-line text-sm" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <button
            onClick={handleNext}
            disabled={thumbStart + visibleCount >= thumbnails.length}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer flex-shrink-0"
            aria-label="다음"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}
